import React from 'react'
import { connect, useSelector } from 'react-redux'

import { selectFullscreen } from '@store/actionSlices/appConfig'
import {
  BucketSource,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionMap,
} from '@store/types'

import DataHandler from '@components/data-handler'
import { useIdleTimeHandler } from '@components/idle-time-handler'
import Skeleton from '@components/skeleton'

import UnitInfoCard from '@pages/properties-view/apartments/unit-view/unit-info-card'

import { selectFromResult, useGetUnitGalleryQuery } from '@api/unit-gallery'

import { CarouselProvider } from '@adUtilities/components/carousel-provider'
import { SlideInterface } from '@adUtilities/constants/carousel'
import { FileType } from '@adUtilities/constants/common'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
} from '@adUtilities/property-gallery'
import { Unit } from '@adUtilities/types/apartment'

interface ExtendedSlideInterface extends SlideInterface {
  originalSrc: string
}

export interface ComparableUnitItemProps {
  unit: Unit
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  appIdleControls: {
    active: boolean
    duration: number
  }
  effectType: string
  autoPlayIntervalInSeconds: number
  showInfo?: boolean
  onlyShowPropertyGalleries?: boolean
}

function ComparableUnitItem({
  unit,
  projectIdentity,
  session,
  appIdleControls,
  effectType,
  autoPlayIntervalInSeconds,
  showInfo,
  onlyShowPropertyGalleries,
}: ComparableUnitItemProps) {
  const fullscreen = useSelector(selectFullscreen)
  const { projectId, projectName } = projectIdentity
  const isAppIdle = useIdleTimeHandler({
    isConnected: !!session?.connected,
    timeoutInSeconds: appIdleControls.duration,
    isIdleActive: appIdleControls.active,
  })

  const unitGalleryQuery = useGetUnitGalleryQuery(
    { projectName, unitId: unit.id },
    { selectFromResult }
  )
  const { unitGalleryData } = unitGalleryQuery

  const buildPropertyGalleryItems = (): PropertyGallery[] =>
    unitGalleryData?.map(({ id, src, label, type, thumbnail }) => ({
      id,
      src,
      thumbnail,
      label,
      type,
      noSpliceUrl: false,
    })) || []

  const buildCarouselItems = (
    propertyGallery: PropertyGallery[]
  ): ExtendedSlideInterface[] =>
    propertyGallery.map(({ id, label, type, src, thumbnail, noSpliceUrl }) => ({
      id,
      label,
      src: type === FileType.Image && src ? src : thumbnail || '',
      thumbnail,
      originalSrc: src,
      bucketType: BucketSource.Legacy,
      fileType: type,
      noSpliceUrl,
    }))

  const slides = React.useMemo(() => {
    const {
      metas: { floorImage, floorLabel, floorPdf, viewLineImages },
    } = unit

    const floorItems: FloorImage[] = floorImage
      ? [
          {
            id: floorPdf,
            src: floorImage,
            label: floorLabel || floorImage,
          },
        ]
      : []

    const propertyGalleryItems = buildPropertyGalleryItems()

    const propertyGallery = mapPropertyGallery({
      projectId,
      floorImages: floorItems,
      viewLineImages: viewLineImages || [],
      propertyGalleries: propertyGalleryItems,
      onlyShowPropertyGallery: Boolean(onlyShowPropertyGalleries),
    })
    const carouselItems = buildCarouselItems(propertyGallery)

    return carouselItems[0] ? [carouselItems[0]] : []
  }, [projectId, unit, unitGalleryData, onlyShowPropertyGalleries])

  return (
    <DataHandler
      payloads={{
        ...unitGalleryQuery,
        data: slides,
      }}
      skeletonFrame={<Skeleton containerClass="!w-full absolute" />}
    >
      <div className="relative flex h-full w-full">
        <div
          className={`pointer-events-none absolute bottom-0 z-20 h-92 w-full bg-shadow-horizontal opacity-75 transition duration-300 ease-in ${
            showInfo ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div
            className={`flex h-full transform items-stretch duration-200 ease-in ${
              fullscreen ? 'pb-2' : 'pb-12'
            } pl-5`}
          >
            <div className="self-end">
              <UnitInfoCard unit={unit} />
            </div>
          </div>
        </div>
        <div className="absolute inset-0">
          <CarouselProvider
            gallerySettings={{ effectType, autoPlayIntervalInSeconds }}
            slides={slides}
            activeSlideIndex={0}
            showControl={false}
            mousewheel={false}
            autoPlay={false}
            isAppIdle={isAppIdle}
          />
        </div>
      </div>
    </DataHandler>
  )
}

export default connect(
  ({
    projectIdentity,
    firestore: { session },
    projectConfig: {
      propertyGalleryTab,
      gallerySettings: { type, intervalInSeconds },
      onlyShowPropertyGalleries,
    },
  }: RootStateTypeExtra) => ({
    projectIdentity,
    session,
    appIdleControls: propertyGalleryTab,
    effectType: type,
    autoPlayIntervalInSeconds: intervalInSeconds,
    onlyShowPropertyGalleries,
  })
)(ComparableUnitItem)
